import first from "lodash/first";
import nth from "lodash/nth";
import type { FunctionComponent } from "react";
import { Suspense, useEffect } from "react";
import { graphql, useFragment, useQueryLoader } from "react-relay";
import { scmpPlus } from "scmp-sdk";

import { notEmpty } from "shared/lib/utils";

import type { issuesNewsletterArticleArticle$key } from "scmp-app/queries/__generated__/issuesNewsletterArticleArticle.graphql";
import type { issuesNewsletterArticleQuery } from "scmp-app/queries/__generated__/issuesNewsletterArticleQuery.graphql";

import { NewsletterArticleIssuesDropdown } from "./issues-dropdown";
import { Placeholder } from "./styles";

type Props = {
  className?: string;
  reference: issuesNewsletterArticleArticle$key;
};

export const query = graphql`
  query issuesNewsletterArticleQuery(
    $typeIds: [String]!
    $first: Int!
    $scmpPlusPaywallTypeIds: [String]
  ) {
    articles(
      filter: { typeIds: $typeIds }
      exclude: { paywallTypeIds: $scmpPlusPaywallTypeIds }
      orderBy: { field: PUBLISHED_DATE, direction: DESC }
      first: $first
    ) {
      ...issuesDropdownNewsletterArticleArticleConnection
    }
  }
`;

export const NewsletterArticleIssues: FunctionComponent<Props> = ({ className, reference }) => {
  const article = useFragment(
    graphql`
      fragment issuesNewsletterArticleArticle on Article {
        types {
          value {
            entityUuid
          }
        }
        ...issuesDropdownNewsletterArticleArticle
      }
    `,
    reference,
  );

  const articleTypeIds = first(article.types?.filter(notEmpty))
    ?.value?.filter(notEmpty)
    .map(type => type?.entityUuid);
  const articleTypeId = nth(articleTypeIds, 1);

  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<issuesNewsletterArticleQuery>(query);

  useEffect(() => {
    if (!articleTypeId) return;
    loadQuery({
      first: 10,
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
      typeIds: [articleTypeId],
    });

    return disposeQuery;
  }, [disposeQuery, loadQuery, articleTypeId]);

  return (
    <div className={className}>
      {!queryReference && <Placeholder />}
      <Suspense fallback={<Placeholder />}>
        {queryReference && (
          <NewsletterArticleIssuesDropdown queryReference={queryReference} reference={article} />
        )}
      </Suspense>
    </div>
  );
};

NewsletterArticleIssues.displayName = "NewsletterArticleIssues";
